import type { Client, CrmLead } from '../services/crmService';
import { normalizeCrmLeadStatus } from '../services/crmService';

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Next-step hint for pipeline contacts (Client stage=lead). */
export function nextBestActionForPipelineClient(c: Client): string {
  const wf = normalizeCrmLeadStatus(c.lead_workflow_status ?? 'new');
  if (wf === 'qualified') return 'Qualified — convert to prospect or client when ready.';
  if (wf === 'unqualified') return 'Unqualified — archive or revisit later.';

  const nextIso = c.next_follow_up_at;
  if (nextIso) {
    const due = new Date(nextIso);
    if (!Number.isNaN(due.getTime())) {
      const today = startOfDay(new Date());
      const dueDay = startOfDay(due);
      if (dueDay < today) return 'Follow up now — overdue.';
      if (dueDay === today) return 'Scheduled for today — reach out.';
    }
  }

  const temp = (c.lead_temperature ?? 'warm').toLowerCase();
  if (temp === 'hot') return 'Hot priority — call or email while interest is high.';
  if (wf === 'new') return 'Qualify fit, confirm contact info, propose a next step.';
  if (wf === 'attempted') return 'Try another channel or leave a clear voicemail.';
  if (wf === 'contacted' || wf === 'replied') return 'Send value, confirm interest, book a concrete next step.';
  return 'Schedule a follow-up or log an activity.';
}

/** Next-step hint for marketing / inbound leads (`crm.Lead`). */
export function nextBestActionForInboundLead(l: CrmLead): string {
  if (l.converted_to_client) return 'Already converted — open in Prospects / Clients.';
  const wf = normalizeCrmLeadStatus(l.status);
  if (wf === 'qualified') return 'Marked qualified — convert to pipeline or client.';
  if (wf === 'unqualified') return 'Unqualified — no further action unless re-engaged.';

  const nextIso = l.next_follow_up_at;
  if (nextIso) {
    const due = new Date(nextIso);
    if (!Number.isNaN(due.getTime())) {
      const today = startOfDay(new Date());
      const dueDay = startOfDay(due);
      if (dueDay < today) return 'Follow up now — overdue.';
      if (dueDay === today) return 'Touch base today.';
    }
  }

  const temp = (l.lead_temperature ?? 'warm').toLowerCase();
  if ((l.score ?? 0) >= 70 || temp === 'hot') return 'High score — prioritize outreach.';
  if (wf === 'new') return 'Acknowledge inquiry and qualify next step.';
  if (wf === 'attempted') return 'Follow up on your last attempt with a different angle.';
  if (wf === 'contacted' || wf === 'replied') return 'Share specifics and propose a concrete next step.';
  return 'Schedule follow-up or send a templated email.';
}
